/**
 * Where the app's AI gets its key. Usually the provider, key and model are in this browser's localStorage and nowhere
 * else. They never go to Taretto's server, so they aren't in its settings, backups, MCP or logs, and each device is set
 * up on its own (a shared iPad shares one). The key goes only to the provider (anthropic.ts, openai.ts). Nothing saved
 * means AI is off.
 *
 * A self-hosted server can be started with a key of its own instead (AI_PROVIDER and AI_KEY, server/routes/ai.ts),
 * which /api/info announces by its provider and model, never the key, or with an OpenAI-compatible server to forward
 * to (AI_BASE_URL), announced by its host too. Then every device uses that through the server, and a key saved here
 * isn't used until the server has none again; Settings › AI offers to forget it.
 */
import { useMemo, useSyncExternalStore } from 'react';
import { useServerInfo } from '@/api/hooks';
import { isAiProviderId } from './providers';
import type { AiConfig } from './types';

export const AI_CONFIG_KEY = 'taretto.ai';

const listeners = new Set<() => void>();
let cache: { raw: string | null; config: AiConfig | null } = { raw: null, config: null };

function readRaw(): string | null {
  try {
    return localStorage.getItem(AI_CONFIG_KEY);
  } catch {
    return null;
  }
}

function parse(raw: string | null): AiConfig | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<Record<keyof AiConfig, unknown>>;
    const { provider, key, model } = value;
    if (!isAiProviderId(provider) || typeof key !== 'string' || !key || typeof model !== 'string' || !model) return null;
    return { provider, key, model };
  } catch {
    return null;
  }
}

/** The config saved on this device, or null. The same object until it changes, as useSyncExternalStore needs. */
export function readAiConfig(): AiConfig | null {
  const raw = readRaw();
  if (raw !== cache.raw) cache = { raw, config: parse(raw) };
  return cache.config;
}

const notify = () => listeners.forEach((listener) => listener());

/** Returns false when the browser won't store it (storage off, private mode on old Safari). */
export function saveAiConfig(config: AiConfig): boolean {
  try {
    localStorage.setItem(AI_CONFIG_KEY, JSON.stringify({ provider: config.provider, key: config.key, model: config.model }));
    return true;
  } catch {
    return false;
  } finally {
    notify();
  }
}

export function forgetAiConfig(): void {
  try {
    localStorage.removeItem(AI_CONFIG_KEY);
  } catch {
    /* storage unavailable: nothing was saved */
  }
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab saved or forgot it.
  const onStorage = (e: StorageEvent) => {
    if (e.key === AI_CONFIG_KEY || e.key === null) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The config saved on this device, used or not. */
export function useSavedAiConfig(): AiConfig | null {
  // The app never renders on a server; tests render to a string, with or without a localStorage.
  return useSyncExternalStore(subscribe, readAiConfig, readAiConfig);
}

/** The server's AI (/api/info) as a config with no key, or null when it has none. The same object while it's the same. */
export function useServerAiConfig(): AiConfig | null {
  const ai = useServerInfo().data?.ai;
  const provider = ai?.provider;
  const model = ai?.model;
  const host = ai?.host;
  return useMemo(
    () => (isAiProviderId(provider) && model ? { provider, key: '', model, server: true, ...(host ? { host } : {}) } : null),
    [provider, model, host],
  );
}

/** The AI this device uses: the server's when it has a key, else the one saved here, or null when AI is off. */
export function useAiConfig(): AiConfig | null {
  const saved = useSavedAiConfig();
  return useServerAiConfig() ?? saved;
}
